import React, { useState } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { Theme } from '@mui/material/styles';
import styled from '@emotion/styled';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  Box,
  Stack,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import grey from '../Palettes/grey';
import colors from '../Theme/colors';
import {
  CheckItem as BaseCheckItem,
  CheckIconWrapper,
} from '../FeatureCheckList';
import CheckIcon from '../Icons/Check';

const CheckItem = styled(BaseCheckItem)`
  margin-bottom: 12px;
`;

const StyledCard = styled(Card)`
  max-width: 28rem;
  margin: 0 auto;
  position: relative;
  overflow: hidden;
`;

const StyledCardHeader = styled(CardHeader)`
  padding: 30px 40px 20px;
  & .MuiCardHeader-content {
    margin: 0 auto;
  }
`;

const HeaderTypography = styled(Typography)`
  font-weight: 800;
  font-size: 1.3rem;
  text-align: center;
  line-height: 1.3;
`;

const StyledButton = styled(Button)<{ theme?: Theme }>`
  width: 100%;
  background-color: ${colors.black};
  color: ${colors.white};
  padding: 12px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s;
  text-transform: none;
  &:hover {
    background-color: rgba(0, 0, 0, 0.9);
    transform: scale(1.01);
    box-shadow: ${({ theme }) => theme.shadows[4]};
  }
`;

const LearnMoreButton = styled(Button)`
  width: 100%;
  margin-top: 8px;
  text-transform: none;
  color: ${colors.black};
  text-decoration: underline;
  &:hover {
    background-color: transparent;
    text-decoration: underline;
  }
`;

const ButtonContent = styled.div<{ theme?: Theme }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const ButtonGlow = styled.div<{ isHovering: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  ${'' /* eslint-disable-next-line @stylistic/max-len */}
  background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.11), transparent);
  transition: all 1.5s;
  transform: ${
    ({ isHovering }) => (isHovering ? 'translateX(100%)' : 'translateX(-100%)')
  }
`;

const InfoText = styled(Typography)`
  margin-top: 8px;
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.6);
`;

const CenteredInfoText = styled(InfoText)`
  text-align: center;
  margin-bottom: 8px;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const sellingPointPropertyTypes = {
  sellingPointMessage: PropTypes.string.isRequired,
};

type SellingPointProperties = InferProps<
typeof sellingPointPropertyTypes
>;

const SellingPoint = ({ sellingPointMessage }: SellingPointProperties) => (
  <CheckItem>
    <CheckIconWrapper>
      <CheckIcon />
    </CheckIconWrapper>
    <Typography>
      {sellingPointMessage}
    </Typography>
  </CheckItem>
);

const BillingComparisonWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 12px;
`;

const BillingCard = styled.div<{ highlighted?: boolean }>`
  border-width: 2px;
  border-style: solid;
  border-radius: 14px;
  padding: 16px;
  position: relative;
  border-color: ${
    ({ highlighted }) => (highlighted ? colors.black : grey[300])
  };
  color: ${grey[600]};
`;

const BillingTerm = styled.div`
  color: ${colors.black};
  font-weight: bold;
`;

const MonthlyPriceWrapper = styled.div`
  line-height: 1.5;
`;

const MonthlyPrice = styled.span`
  color: ${colors.black};
  font-size: 1.5rem;
  font-weight: bold;
`;

const MonthlyDenominator = styled.span`
  font-weight: bold;
`;

const BillingCost = styled.div`
  font-size: 0.875rem;
  color: ${grey[600]};
  line-height: 1.5;
`;

const Savings = styled.div`
  font-size: 0.875rem;
  color: ${colors.discount};
  line-height: 1.5;
  font-weight: bold;
`;

const BestValueBadge = styled.div`
  font-size: 0.875rem;
  font-weight: bold;
  position: absolute;
  height: 28px;
  border-radius: 14px;
  width: auto;
  padding: 0 12px;
  left: 50%;
  transform: translateX(-50%);
  top: -14px;
  color: white;
  background-color: ${colors.discount};
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const subscriptionCallToActionPropertyTypes = {
  callToActionMessage: PropTypes.string.isRequired,
  sellingPoints: PropTypes.arrayOf(
    PropTypes.string.isRequired,
  ).isRequired,
  youCanCancelMessage: PropTypes.string.isRequired,
  startReadingMessage: PropTypes.string.isRequired,
  learnMoreMessage: PropTypes.string.isRequired,
  complimentaryAccessMessage: PropTypes.string.isRequired,
  subscriptionPriceMessage: PropTypes.string.isRequired,
  yearlyMessage: PropTypes.string.isRequired,
  monthlyMessage: PropTypes.string.isRequired,
  savePerYearMessage: PropTypes.string.isRequired,
  perMonthAbbreviationMessage: PropTypes.string.isRequired,
  billedAnnuallyMessage: PropTypes.string.isRequired,
  bestValueMessage: PropTypes.string.isRequired,
  billedMonthlyMessage: PropTypes.string.isRequired,
  annualBillingMonthlyEquivalent: PropTypes.string.isRequired,
  monthlyBillingPrice: PropTypes.string.isRequired,
  learnMoreRoute: PropTypes.string.isRequired,
  LinkComponent: PropTypes.elementType.isRequired,
  onClick: PropTypes.func.isRequired,
};

type SubscriptionCallToActionProperties = InferProps<
  typeof subscriptionCallToActionPropertyTypes
>;

const SubscriptionCallToAction = ({
  callToActionMessage,
  sellingPoints,
  youCanCancelMessage,
  startReadingMessage,
  learnMoreMessage,
  complimentaryAccessMessage,
  subscriptionPriceMessage,
  yearlyMessage,
  monthlyMessage,
  savePerYearMessage,
  perMonthAbbreviationMessage,
  billedAnnuallyMessage,
  bestValueMessage,
  billedMonthlyMessage,
  annualBillingMonthlyEquivalent,
  monthlyBillingPrice,
  learnMoreRoute,
  LinkComponent,
  onClick,
}: SubscriptionCallToActionProperties) => {
  const [isHovering, setIsHovering] = useState(false);

  return (
    <StyledCard>
      <StyledCardHeader
        title={(
          <HeaderTypography>
            { callToActionMessage }
          </HeaderTypography>
        )}
      />
      <BillingComparisonWrapper>
        <BillingCard highlighted>
          <BillingTerm>{ yearlyMessage }</BillingTerm>
          <MonthlyPriceWrapper>
            <MonthlyPrice>{ annualBillingMonthlyEquivalent }</MonthlyPrice>
            <MonthlyDenominator>
              /
              { perMonthAbbreviationMessage }
            </MonthlyDenominator>
          </MonthlyPriceWrapper>
          <BillingCost>{ billedAnnuallyMessage }</BillingCost>
          <Savings>{ savePerYearMessage }</Savings>
          <BestValueBadge>{ bestValueMessage }</BestValueBadge>
        </BillingCard>
        <BillingCard>
          <BillingTerm>{ monthlyMessage }</BillingTerm>
          <MonthlyPriceWrapper>
            <MonthlyPrice>{ monthlyBillingPrice }</MonthlyPrice>
            <MonthlyDenominator>
              /
              { perMonthAbbreviationMessage }
            </MonthlyDenominator>
          </MonthlyPriceWrapper>
          <BillingCost>{ billedMonthlyMessage }</BillingCost>
        </BillingCard>
      </BillingComparisonWrapper>

      <CardContent sx={{ pt: 2 }}>
        <Stack spacing={3}>
          <Box>
            {sellingPoints.map((sellingPoint: string) => (
              <SellingPoint
                key={sellingPoint}
                sellingPointMessage={sellingPoint}
              />
            ))}
          </Box>

          <Box>
            <CenteredInfoText>
              { youCanCancelMessage }
            </CenteredInfoText>
            <LinkComponent route="subscribe">
              <StyledButton
                variant="contained"
                size="large"
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                onClick={onClick}
                data-testid={
                  `subscription-button-${isHovering ? 'hovering' : 'default'}`
                }
              >
                <ButtonContent>
                  { startReadingMessage }
                  {' '}
                  { monthlyBillingPrice }
                  <ArrowForwardIcon
                    sx={{
                      fontSize: '1rem',
                      opacity: isHovering ? 1 : 0,
                      transform: isHovering ? 'translate(4px)' : 'translate(0)',
                      transition: 'all 0.1s',
                    }}
                  />
                </ButtonContent>
                <ButtonGlow isHovering={isHovering} />
              </StyledButton>
            </LinkComponent>
            <LinkComponent route={learnMoreRoute}>
              <LearnMoreButton
                variant="text"
                data-testid="subscription-learn-more-button"
              >
                { learnMoreMessage }
              </LearnMoreButton>
            </LinkComponent>
          </Box>

          <Box>
            <InfoText>
              { complimentaryAccessMessage }
            </InfoText>
            <InfoText sx={{ marginTop: 1 }}>
              { subscriptionPriceMessage }
            </InfoText>
          </Box>
        </Stack>
      </CardContent>
    </StyledCard>
  );
};

export default SubscriptionCallToAction;
